// Merkan · Entwürfe auf dem Server (Migration 0068)
//
// Angefangene Antworten, Weiterleitungen, Notizen, neue Mails, Telefonnotizen und Vorgänge bleiben als Entwurf
// erhalten, auch über Gerätewechsel hinweg, bis sie gesendet bzw. angelegt oder ausdrücklich verworfen werden.
// Ein Entwurf ist je Benutzer, Mandant, Art und Bezug eindeutig (z. B. art 'antworten', bezug = Nachrichten-ID).
//
//   const e = useEntwurf<MeinTyp>('antworten', nachrichtId);
//   e.geladen / e.daten     nach dem Laden der gespeicherte Stand (oder null)
//   e.merken(daten)         verzögert speichern (800 ms nach der letzten Änderung, beim Schließen sofort)
//   e.verwerfen()           löschen (Knopf „Entwurf verwerfen“ oder nach dem Senden)
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from './supabase';
import { useMandantId, useSession } from './session';

export type EntwurfArt = 'antworten' | 'weiterleiten' | 'notiz' | 'mail' | 'telefon' | 'vorgang';

const EREIGNIS = 'merkan-entwuerfe';
const melden = () => { try { window.dispatchEvent(new Event(EREIGNIS)); } catch { /* egal */ } };

export function useEntwurf<T extends object>(art: EntwurfArt, bezug: string | null | undefined) {
  const mandantId = useMandantId();
  const { session } = useSession();
  const ich = session?.user.id ?? '';
  const schluessel = bezug ?? '-';
  const [geladen, setGeladen] = useState(false);
  const [daten, setDaten] = useState<T | null>(null);
  const [gespeichertAm, setGespeichertAm] = useState<string | null>(null);
  const offen = useRef<T | null>(null);
  const takt = useRef<number | null>(null);
  const weg = useRef(false);

  useEffect(() => {
    let aktiv = true;
    weg.current = false;
    setGeladen(false);
    void supabase.from('entwuerfe').select('daten, updated_at').eq('mandant_id', mandantId).eq('user_id', ich).eq('art', art).eq('bezug', schluessel).maybeSingle()
      .then(({ data }) => {
        if (!aktiv) return;
        const d = data as { daten: T; updated_at: string } | null;
        setDaten(d?.daten ?? null);
        setGespeichertAm(d?.updated_at ?? null);
        setGeladen(true);
      });
    return () => { aktiv = false; };
  }, [mandantId, ich, art, schluessel]);

  const schreiben = useCallback(async (d: T) => {
    if (weg.current || !ich) return;
    const { error } = await supabase.from('entwuerfe').upsert({ mandant_id: mandantId, user_id: ich, art, bezug: schluessel, daten: d }, { onConflict: 'mandant_id,user_id,art,bezug' });
    if (!error) { setGespeichertAm(new Date().toISOString()); setDaten(d); melden(); }
  }, [mandantId, ich, art, schluessel]);

  const merken = useCallback((d: T) => {
    offen.current = d;
    if (takt.current) window.clearTimeout(takt.current);
    takt.current = window.setTimeout(() => { const x = offen.current; offen.current = null; takt.current = null; if (x) void schreiben(x); }, 800);
  }, [schreiben]);

  // Beim Schließen oder Seitenwechsel ausstehende Änderungen sofort sichern
  useEffect(() => () => {
    if (takt.current) window.clearTimeout(takt.current);
    const x = offen.current;
    if (x) void schreiben(x);
  }, [schreiben]);

  const verwerfen = useCallback(async () => {
    weg.current = true;
    offen.current = null;
    if (takt.current) window.clearTimeout(takt.current);
    await supabase.from('entwuerfe').delete().eq('mandant_id', mandantId).eq('user_id', ich).eq('art', art).eq('bezug', schluessel);
    setDaten(null);
    setGespeichertAm(null);
    melden();
  }, [mandantId, ich, art, schluessel]);

  return { geladen, daten, gespeichertAm, merken, verwerfen };
}

/** Bezüge (z. B. Nachrichten-IDs) mit eigenem Entwurf, für Marken in Listen; aktualisiert sich bei jeder Änderung */
export function useEntwurfBezuege(arten: EntwurfArt[]): Set<string> {
  const mandantId = useMandantId();
  const { session } = useSession();
  const ich = session?.user.id ?? '';
  const [liste, setListe] = useState<Set<string>>(new Set());
  const artenText = arten.join(',');
  useEffect(() => {
    let aktiv = true;
    const laden = () => void supabase.from('entwuerfe').select('bezug').eq('mandant_id', mandantId).eq('user_id', ich).in('art', artenText.split(','))
      .then(({ data }) => { if (aktiv) setListe(new Set(((data as { bezug: string }[] | null) ?? []).map((x) => x.bezug))); });
    laden();
    window.addEventListener(EREIGNIS, laden);
    return () => { aktiv = false; window.removeEventListener(EREIGNIS, laden); };
  }, [mandantId, ich, artenText]);
  return liste;
}

/** Lokale Bedienvorlieben, die auf allen Geräten gelten (Menü eingeklappt, Ansichten, letzter Reiter) */
export function useVorliebe<T extends string>(schluessel: string, standard: T): [T, (v: T) => void] {
  const { einstellungen, einstellungSetzen } = useSession();
  const server = (einstellungen.oberflaeche ?? {})[schluessel] as T | undefined;
  const lokal = (() => { try { return (localStorage.getItem(`merkan.${schluessel}`) as T | null) ?? undefined; } catch { return undefined; } })();
  const wert = server ?? lokal ?? standard;
  const setzen = useCallback((v: T) => {
    try { localStorage.setItem(`merkan.${schluessel}`, v); } catch { /* egal */ }
    void einstellungSetzen({ oberflaeche: { ...(einstellungen.oberflaeche ?? {}), [schluessel]: v } });
  }, [schluessel, einstellungen.oberflaeche, einstellungSetzen]);
  return [wert, setzen];
}
